<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-large"
    title="Oracle Cloud向けVNコネクト設定"
  >
    <template v-if="isLoaded" v-slot:body="{ cancel }">
      <cross-validation-observer
        :initial-values="{
          routeAggregationAddressList:
            form.cloudVnConnectCommon!.routeAggregationAddressList &&
            form.cloudVnConnectCommon!.routeAggregationAddressList.length > 0
              ? form.cloudVnConnectCommon!.routeAggregationAddressList
              : [''],
        }"
        v-slot="{ handleSubmit }"
        as="div"
      >
        <v-form novalidate @submit.stop.prevent="handleSubmit(submit)">
          <v-card-text class="pa-0">
            <div class="modal-body">
              <v-container>
                <!-- クラウド設定 -->
                <v-row>
                  <v-col>
                    <h3>クラウド設定</h3>
                  </v-col>
                </v-row>
                <!-- OCID(Act) -->
                <v-row>
                  <v-col>
                    <app-badge
                      item="OCID(Act)"
                      :is-info="isAdd"
                      msg="先頭がocid"
                      :isRequired="isAdd"
                    />
                    <template v-if="isAdd">
                      <validation-provider
                        name="ocidAct"
                        label="OCID(Act)"
                        v-model="
                          (form as CloudVnOraclePost).cloudVnConnectAct!
                            .serviceKey
                        "
                        :rules="{
                          required: true,
                          is_ocid_start: true,
                        }"
                        v-slot="{ componentField, errorMessage, meta }"
                      >
                        <app-form-input
                          v-bind="componentField"
                          :field-meta="meta"
                          :error-messages="errorMessage"
                        />
                      </validation-provider>
                    </template>
                    <app-form-input
                      v-else
                      :value="cloudVnOracleDetail!.cloudVnConnectAct.serviceKey"
                      disabled
                    />
                  </v-col>
                </v-row>
                <!-- OCID(Sby) -->
                <v-row>
                  <v-col>
                    <app-badge
                      item="OCID(Sby)"
                      :is-info="isAdd || !hasSby"
                      msg="先頭がocid"
                      :isRequired="false"
                    />
                    <template v-if="isAdd || !hasSby">
                      <validation-provider
                        name="ocidSby"
                        label="OCID(Sby)"
                        v-model="
                          (form as CloudVnOraclePost).cloudVnConnectSby!
                            .serviceKey
                        "
                        :rules="{
                          is_ocid_start: true,
                          is_not_eq: {
                            target: '@ocidAct',
                            targetField: 'OCID(Act)',
                          },
                        }"
                        v-slot="{ componentField, errorMessage, meta }"
                      >
                        <app-form-input
                          v-bind="componentField"
                          :field-meta="meta"
                          :error-messages="errorMessage"
                        />
                      </validation-provider>
                    </template>
                    <app-form-input
                      v-else
                      :value="
                        cloudVnOracleDetail!.cloudVnConnectSby!.serviceKey
                      "
                      disabled
                    />
                  </v-col>
                </v-row>
                <!--ロケーション-->
                <v-row>
                  <v-col>
                    <app-badge
                      item="ロケーション"
                      :is-info="false"
                      :isRequired="isAdd"
                    />
                    <template v-if="isAdd">
                      <validation-provider
                        name="location"
                        label="ロケーション"
                        v-model="
                          (form as CloudVnOraclePost).cloudVnConnectCommon!
                            .location
                        "
                        :rules="{ required: true }"
                        v-slot="{ componentField, errorMessage, meta }"
                      >
                        <app-select
                          :items="locationOptions"
                          item-title="text"
                          item-value="value"
                          v-bind="componentField"
                          :field-meta="meta"
                          :error-messages="errorMessage"
                        />
                      </validation-provider>
                    </template>
                    <app-form-input
                      v-else
                      :value="
                        $filter.enumTo(
                          cloudVnOracleDetail!.cloudVnConnectCommon.location,
                          'location'
                        )
                      "
                      disabled
                    />
                  </v-col>
                </v-row>
                <!--接続先仮想ネットワーク(VPN/VN)名-->
                <v-row>
                  <v-col>
                    <app-badge
                      item="接続先仮想ネットワーク(VPN/VN)名"
                      :is-info="false"
                      :isRequired="isAdd"
                    />
                    <template v-if="isAdd">
                      <validation-provider
                        name="selectedVpnVn"
                        label="接続先仮想ネットワーク(VPN/VN)名"
                        v-model="selectedVpnVn"
                        :rules="{ required: true }"
                        v-slot="{ componentField, errorMessage, meta }"
                      >
                        <app-select-search
                          :items="vpnVnCodeSelectList"
                          item-title="text"
                          item-value="value"
                          v-bind="componentField"
                          :field-meta="meta"
                          :error-messages="errorMessage"
                        />
                      </validation-provider>
                    </template>
                    <!--変更の場合、変更不可-->
                    <app-form-input v-else :value="vpnVnName" disabled />
                  </v-col>
                </v-row>

                <!-- VNコネクト設定 -->
                <v-row>
                  <v-col>
                    <h3>VNコネクト設定</h3>
                  </v-col>
                </v-row>
                <!--品目-->
                <v-row>
                  <v-col>
                    <app-badge
                      item="品目"
                      :is-info="false"
                      :isRequired="true"
                    />
                    <validation-provider
                      name="bandwidth"
                      label="品目"
                      v-model="form.cloudVnConnectCommon!.bandwidth"
                      :rules="{ required: true }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-select
                        :items="bandwidthSelectList"
                        item-title="text"
                        item-value="value"
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
                <!--VNコネクト名(Act)-->
                <v-row>
                  <v-col>
                    <app-badge
                      item="VNコネクト名(Act)"
                      :msg="
                        $filter.message('info_max_length', {
                          maxLength: '20',
                        })
                      "
                      :is-info="isVnConnectNameActEnable"
                      :isRequired="isVnConnectNameActEnable"
                    />
                    <validation-provider
                      name="vnConnectNameAct"
                      label="VNコネクト名(Act)"
                      v-model="form.cloudVnConnectAct!.vnConnectName"
                      :rules="
                        isVnConnectNameActEnable
                          ? {
                              required: true,
                              max: { length: 20 },
                              alpha_num_zenkaku: true,
                            }
                          : undefined
                      "
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        :disabled="!isVnConnectNameActEnable"
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
                <!--VNコネクト名(Sby)-->
                <v-row>
                  <v-col>
                    <app-badge
                      item="VNコネクト名(Sby)"
                      :msg="
                        $filter.message('info_max_length', {
                          maxLength: '20',
                        })
                      "
                      :is-info="isRequireRoutingSby && isVnConnectNameSbyEnable"
                      :isRequired="
                        isRequireRoutingSby && isVnConnectNameSbyEnable
                      "
                    />
                    <validation-provider
                      name="vnConnectNameSby"
                      label="VNコネクト名(Sby)"
                      v-model="form.cloudVnConnectSby!.vnConnectName"
                      :rules="
                        isRequireRoutingSby && isVnConnectNameSbyEnable
                          ? {
                              required: true,
                              max: { length: 20 },
                              alpha_num_zenkaku: true,
                            }
                          : undefined
                      "
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        :disabled="
                          isDisableRoutingSby || !isVnConnectNameSbyEnable
                        "
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>

                <!-- ルーティング情報(共通) -->
                <v-row>
                  <v-col>
                    <h3>ルーティング情報(共通)</h3>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <app-label-group label="ルーティング種別" value="BGP4" />
                  </v-col>
                </v-row>
                <!--AS番号-->
                <v-row>
                  <v-col>
                    <app-badge
                      item="AS番号"
                      :is-info="false"
                      :isRequired="false"
                    />
                    <app-form-input
                      :value="
                        (form as CloudVnOraclePost).cloudVnConnectAct!.asNumber
                      "
                      disabled
                    />
                  </v-col>
                </v-row>
                <!--Maximum Prefix-->
                <v-row>
                  <v-col>
                    <app-badge
                      item="Maximum Prefix"
                      :is-info="false"
                      :isRequired="true"
                    />
                    <validation-provider
                      name="maximumPrefix"
                      label="Maximum Prefix(Act)"
                      v-model="form.cloudVnConnectAct!.maximumPrefix"
                      :rules="{ required: true }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-select
                        :items="maximumPrefixSelectList"
                        item-title="text"
                        item-value="value"
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>

                <!-- ルーティング情報(Act) -->
                <v-row>
                  <v-col>
                    <h3>ルーティング情報(Act)</h3>
                  </v-col>
                </v-row>
                <!--WANアドレス(Act)-->
                <v-row>
                  <v-col>
                    <app-badge
                      item="WANアドレス"
                      :msg="
                        '・' +
                        $filter.message('info_cidr_ip_v4_with_subnet_2', {
                          subnet: '30',
                        }) +
                        '\n' +
                        '・' +
                        $filter.message('info_cidr_subnet', {
                          subnet: '16-/30',
                        })
                      "
                      :is-info="isAdd"
                      :is-required="isAdd"
                    />
                    <validation-provider
                      name="wanAddressAct"
                      label="WANアドレス(Act)"
                      v-model="
                        (form as CloudVnOraclePost).cloudVnConnectAct!
                          .wanAddress
                      "
                      :rules="{
                        required: true,
                        is_ip_subnet: { min: 16, max: 30 },
                        is_not_current_ip: true,
                        is_not_loopback_ip: true,
                        is_not_multicast_ip: true,
                        is_not_class_e_ip: true,
                        is_not_broadcast_ip: true,
                        is_not_network_ip: true,
                      }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        :disabled="!isAdd"
                        :placeholder="
                          !isAdd
                            ? null
                            : $filter.message(
                                'placeholder_ip_address_with_subnet30'
                              )
                        "
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
                <!--ピアIPアドレス(Act)-->
                <v-row>
                  <v-col>
                    <app-badge
                      item="ピアIPアドレス"
                      :msg="
                        $filter.message('info_ip_address_v4_2', { host: '2' })
                      "
                      :is-info="isAdd"
                      :is-required="isAdd"
                    />
                    <validation-provider
                      name="peerIpAddressAct"
                      label="ピアIPアドレス(Act)"
                      v-model="
                        (form as CloudVnOraclePost).cloudVnConnectAct!
                          .peerIpAddress
                      "
                      :rules="{
                        required: true,
                        is_ipv4: true,
                        cross_wan_address_within_range: {
                          wan: (form as CloudVnOraclePost).cloudVnConnectAct!
                            .wanAddress,
                        },
                        is_not_loopback_ip: true,
                        is_not_multicast_ip: true,
                        is_not_class_e_ip: true,
                        is_not_broadcast_ip: {
                          range: (form as CloudVnOraclePost).cloudVnConnectAct!
                            .wanAddress,
                        },
                        is_not_network_ip: {
                          range: (form as CloudVnOraclePost).cloudVnConnectAct!
                            .wanAddress,
                        },
                      }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        :disabled="!isAdd"
                        :placeholder="
                          !isAdd
                            ? null
                            : $filter.message('placeholder_ip_address_3', {
                                host: '2',
                              })
                        "
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
                <!--Local Preference(Act)-->
                <v-row>
                  <v-col>
                    <app-label-group label="Local Preference">
                      <template #value>
                        <app-form-input
                          :value="
                            $filter.enumTo('HIGH_PRIORITY', 'localPreference')
                          "
                          disabled
                        />
                      </template>
                    </app-label-group>
                  </v-col>
                </v-row>
                <!--MED(Act)-->
                <v-row>
                  <v-col>
                    <app-label-group label="MED">
                      <template #value>
                        <app-form-input
                          :value="$filter.enumTo('LOW_PRIORITY', 'med')"
                          disabled
                        />
                      </template>
                    </app-label-group>
                  </v-col>
                </v-row>

                <!-- ルーティング情報(Sby) -->
                <v-row>
                  <v-col>
                    <h3>ルーティング情報(Sby)</h3>
                  </v-col>
                </v-row>
                <!--WANアドレス(Sby)-->
                <v-row>
                  <v-col>
                    <app-badge
                      item="WANアドレス"
                      :msg="
                        '・' +
                        $filter.message('info_cidr_ip_v4_with_subnet_2', {
                          subnet: '30',
                        }) +
                        '\n' +
                        '・' +
                        $filter.message('info_cidr_subnet', {
                          subnet: '16-/30',
                        })
                      "
                      :is-info="!(isDisableRoutingSby || hasSby)"
                      :isRequired="!(isDisableRoutingSby || hasSby)"
                    />
                    <validation-provider
                      name="wanAddressSby"
                      label="WANアドレス(Sby)"
                      v-model="
                        (form as CloudVnOraclePost).cloudVnConnectSby!
                          .wanAddress
                      "
                      :rules="
                        isRequireRoutingSby
                          ? {
                              required: true,
                              is_ip_subnet: { min: 16, max: 30 },
                              is_not_current_ip: true,
                              is_not_loopback_ip: true,
                              is_not_multicast_ip: true,
                              is_not_class_e_ip: true,
                              is_not_broadcast_ip: true,
                              is_not_network_ip: true,
                            }
                          : ''
                      "
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        :disabled="isDisableRoutingSby || hasSby"
                        :placeholder="
                          isDisableRoutingSby || hasSby
                            ? null
                            : $filter.message(
                                'placeholder_ip_address_with_subnet30'
                              )
                        "
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
                <!--ピアIPアドレス(Sby)-->
                <v-row>
                  <v-col>
                    <app-badge
                      item="ピアIPアドレス"
                      :msg="
                        $filter.message('info_ip_address_v4_2', { host: '2' })
                      "
                      :is-info="!(isDisableRoutingSby || hasSby)"
                      :isRequired="!(isDisableRoutingSby || hasSby)"
                    />
                    <validation-provider
                      name="peerIpAddressSby"
                      label="ピアIPアドレス(Sby)"
                      v-model="
                        (form as CloudVnOraclePost).cloudVnConnectSby!
                          .peerIpAddress
                      "
                      :rules="
                        isRequireRoutingSby
                          ? {
                              required: true,
                              is_ipv4: true,
                              cross_wan_address_within_range: {
                                wan: (form as CloudVnOraclePost)
                                  .cloudVnConnectSby!.wanAddress,
                              },
                              is_not_loopback_ip: true,
                              is_not_multicast_ip: true,
                              is_not_class_e_ip: true,
                              is_not_broadcast_ip: {
                                range: (form as CloudVnOraclePost)
                                  .cloudVnConnectSby!.wanAddress,
                              },
                              is_not_network_ip: {
                                range: (form as CloudVnOraclePost)
                                  .cloudVnConnectSby!.wanAddress,
                              },
                            }
                          : ''
                      "
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        :disabled="isDisableRoutingSby || hasSby"
                        :placeholder="
                          isDisableRoutingSby || hasSby
                            ? null
                            : $filter.message('placeholder_ip_address_3', {
                                host: '2',
                              })
                        "
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
                <!--Local Preference(Sby)-->
                <v-row>
                  <v-col>
                    <app-label-group label="Local Preference">
                      <template #value>
                        <app-form-input
                          :value="
                            $filter.enumTo('LOW_PRIORITY', 'localPreference')
                          "
                          disabled
                        />
                      </template>
                    </app-label-group>
                  </v-col>
                </v-row>
                <!--MED(Sby)-->
                <v-row>
                  <v-col>
                    <app-label-group label="MED">
                      <template #value>
                        <app-form-input
                          :value="$filter.enumTo('HIGH_PRIORITY', 'med')"
                          disabled
                        />
                      </template>
                    </app-label-group>
                  </v-col>
                </v-row>

                <!-- カスタム経路集約情報 -->
                <v-row>
                  <v-col>
                    <h3>カスタム経路集約情報</h3>
                  </v-col>
                </v-row>
                <!--経路集約-->
                <v-row>
                  <v-col>
                    <app-badge
                      item="経路集約"
                      :isRequired="false"
                      :isInfo="false"
                    />
                    <v-radio-group
                      v-model="form.cloudVnConnectCommon!.routeAggregation"
                      hide-details
                      inline
                    >
                      <v-radio
                        v-for="(routeOption, index) in routeOptions"
                        :key="`${routeOption.text + index}`"
                        :label="routeOption.text"
                        :value="routeOption.value"
                      />
                    </v-radio-group>
                  </v-col>
                </v-row>
                <!--経路集約アドレス-->
                <v-row
                  v-if="form.cloudVnConnectCommon!.routeAggregation !== 'OFF'"
                >
                  <v-col>
                    <app-badge
                      item="経路集約アドレス"
                      :is-required="
                        form.cloudVnConnectCommon!.routeAggregation === 'CUSTOM'
                      "
                      :is-info="
                        form.cloudVnConnectCommon!.routeAggregation === 'CUSTOM'
                      "
                      :msg="$filter.message('info_routing_address')"
                    />
                    <template
                      v-if="
                        form.cloudVnConnectCommon!.routeAggregation === 'ON'
                      "
                    >
                      <span class="ml-3">0.0.0.0/0</span>
                    </template>
                    <template
                      v-if="
                        form.cloudVnConnectCommon!.routeAggregation === 'CUSTOM'
                      "
                    >
                      <validation-array
                        name="routeAggregationAddressList"
                        v-slot="{ fields, push, remove }"
                      >
                        <template
                          v-for="(item, index) in fields"
                          :key="item.key"
                        >
                          <v-row class="ga-2">
                            <validation-provider
                              :name="`routingIp_${item.key}`"
                              label="経路集約アドレス"
                              v-model="
                                form.cloudVnConnectCommon!
                                  .routeAggregationAddressList![index]
                              "
                              :rules="{
                                required: true,
                                is_ip_subnet: { min: 1, max: 30 },
                                is_network_address: true,
                                is_not_default_root: true,
                                duplicate: { list: routingAddressList(index) },
                              }"
                              v-slot="{ componentField, errorMessage, meta }"
                            >
                              <app-form-input
                                class="ml-1"
                                :placeholder="
                                  $filter.message(
                                    'placeholder_ip_address_with_subnet24'
                                  )
                                "
                                v-bind="componentField"
                                :field-meta="meta"
                                :error-messages="errorMessage"
                              />
                              <v-btn
                                icon="$mdiMinus"
                                size="small"
                                variant="outlined"
                                class="rounded-sm ml-2 mr-3"
                                @click="removeRouteAddress(index, remove)"
                              />
                            </validation-provider>
                          </v-row>
                        </template>
                        <!--経路集約アドレス追加-CUSTOMのみ表示-->
                        <v-row justify="end">
                          <v-btn
                            icon="$mdiPlus"
                            size="small"
                            variant="outlined"
                            class="rounded-sm ml-2 mr-3"
                            :disabled="currentLength >= 100"
                            @click="addRouteAddress(push)"
                          />
                        </v-row>
                      </validation-array>
                    </template>
                  </v-col>
                </v-row>

                <!--備考-->
                <v-row>
                  <v-col>
                    <app-badge
                      item="備考"
                      :msg="
                        $filter.message('info_max_length', { maxLength: '20' })
                      "
                      :is-info="true"
                      :isRequired="false"
                    />
                    <validation-provider
                      name="description"
                      label="備考"
                      v-model="form.cloudVnConnectCommon!.description"
                      :rules="{ max: { length: 20 } }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
              </v-container>
            </div>

            <cross-validation-provider
              vid="can_not_save_act_and_create_sby"
              :passed="multipleOption"
              class="my-4"
            >
              {{ $filter.message("can_not_save_act_and_create_sby") }}
            </cross-validation-provider>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-list-item class="w-100 pa-1">
              <template v-slot:append>
                <v-btn type="submit" color="primary" variant="flat">{{
                  isAdd ? "追加" : "変更"
                }}</v-btn>
                <v-btn color="primary" variant="outlined" @click="cancel"
                  >キャンセル
                </v-btn>
              </template>
            </v-list-item>
          </v-card-actions>
        </v-form>
      </cross-validation-observer>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import { VueFinalModal } from "vue-final-modal";
import type {
  BandWidth,
  RouteAggregation,
  VpnVnCode as CloudVpnVnCode,
} from "@/apis/CloudVnMsApi";
import type { CloudLine } from "@/apis/CloudLineApi";
import cloneDeep from "lodash/cloneDeep";
import type { VpnVnCode } from "@/apis/InformationApi";
import type {
  CloudVnOracleDetail,
  CloudVnOraclePost,
  CloudVnOraclePut,
} from "@/apis/CloudVnOracleApi";
import MultiCloudOracleVnConnectionConfirm from "@/modals/multicloud/oracle/MultiCloudOracleVnConnectionConfirm.vue";
import type { UpperLimitEntity } from "@/apis/UpperLimitApi";

export default defineComponent({
  name: "MultiCloudOracleVnLineEdit",
  props: {
    /** クラウド回線SEQ */
    cloudLineSeq: {
      type: String as PropType<string>,
      required: true,
    },
    // 変更の場合用
    cloudVnOracleDetail: {
      type: Object as PropType<CloudVnOracleDetail>,
      default: undefined,
    },
    // 追加の場合用
    cloudVnOracleKeyId: {
      type: String as PropType<string>,
      default: undefined,
    },
    remainingBandwidth: {
      type: Number as PropType<number>,
      required: true,
    },
  },
  data() {
    // 新規/更新のformデータを作成
    const form:
      | CloudVnOraclePost
      | (CloudVnOraclePut & {
          cloudVnConnectSby: { serviceKey: string; asNumber: number };
        }) = (() => {
      if (this.cloudVnOracleDetail) {
        // 変更
        const cloudVnOracleDetailCopy = cloneDeep(this.cloudVnOracleDetail);
        // 経路集約アドレスを数値昇順でソート
        if (
          cloudVnOracleDetailCopy.cloudVnConnectCommon &&
          Array.isArray(
            cloudVnOracleDetailCopy.cloudVnConnectCommon
              .routeAggregationAddressList
          )
        ) {
          cloudVnOracleDetailCopy.cloudVnConnectCommon.routeAggregationAddressList =
            cloudVnOracleDetailCopy.cloudVnConnectCommon.routeAggregationAddressList.sortIpAddress();
        }
        const form: CloudVnOraclePut & {
          cloudVnConnectSby: { serviceKey: string; asNumber: number };
        } = {
          cloudVnConnectCommon: cloudVnOracleDetailCopy.cloudVnConnectCommon,
          cloudVnConnectAct: cloudVnOracleDetailCopy.cloudVnConnectAct,
          cloudVnConnectSby: cloudVnOracleDetailCopy.cloudVnConnectSby
            ? cloudVnOracleDetailCopy.cloudVnConnectSby
            : ({
                asNumber: 31898,
                maximumPrefix: 100,
              } as CloudVnOraclePut["cloudVnConnectSby"] & {
                serviceKey: string;
                asNumber: number;
              }),
          cloudVnOracleKeyId: cloudVnOracleDetailCopy.cloudVnOracleKeyId,
        };
        return form;
      } else {
        // 追加
        const form: CloudVnOraclePost = {
          cloudVnConnectCommon: {
            bandwidthType: "PF",
            routeAggregation: "OFF",
            routeAggregationAddressList: [""],
            location: "EAST_JAPAN",
          } as CloudVnOraclePost["cloudVnConnectCommon"],
          cloudVnConnectAct: {
            asNumber: 31898,
            maximumPrefix: 100,
          } as CloudVnOraclePost["cloudVnConnectAct"],
          cloudVnConnectSby: {
            asNumber: 31898,
            maximumPrefix: 100,
          } as CloudVnOraclePost["cloudVnConnectSby"],
          cloudVnOracleKeyId: this.cloudVnOracleKeyId!,
        };
        return form;
      }
    })();

    return {
      form,
      isLoaded: false,
      vpnVnCodeList: [] as VpnVnCode[],
      cloudLineMaxBandwidth: {} as CloudLine["bandwidth"],
      selectedVpnVn: null as null | CloudVpnVnCode,
      bandwidthOptions: [
        { text: "10Mbps", value: { value: 10, unit: "MBPS" } },
        { text: "20Mbps", value: { value: 20, unit: "MBPS" } },
        { text: "30Mbps", value: { value: 30, unit: "MBPS" } },
        { text: "40Mbps", value: { value: 40, unit: "MBPS" } },
        { text: "50Mbps", value: { value: 50, unit: "MBPS" } },
        { text: "60Mbps", value: { value: 60, unit: "MBPS" } },
        { text: "70Mbps", value: { value: 70, unit: "MBPS" } },
        { text: "80Mbps", value: { value: 80, unit: "MBPS" } },
        { text: "90Mbps", value: { value: 90, unit: "MBPS" } },
        { text: "100Mbps", value: { value: 100, unit: "MBPS" } },
        { text: "200Mbps", value: { value: 200, unit: "MBPS" } },
        { text: "300Mbps", value: { value: 300, unit: "MBPS" } },
        { text: "400Mbps", value: { value: 400, unit: "MBPS" } },
        { text: "500Mbps", value: { value: 500, unit: "MBPS" } },
        { text: "600Mbps", value: { value: 600, unit: "MBPS" } },
        { text: "700Mbps", value: { value: 700, unit: "MBPS" } },
        { text: "800Mbps", value: { value: 800, unit: "MBPS" } },
        { text: "900Mbps", value: { value: 900, unit: "MBPS" } },
        { text: "1Gbps", value: { value: 1, unit: "GBPS" } },
        { text: "2Gbps", value: { value: 2, unit: "GBPS" } },
        { text: "3Gbps", value: { value: 3, unit: "GBPS" } },
        { text: "4Gbps", value: { value: 4, unit: "GBPS" } },
        { text: "5Gbps", value: { value: 5, unit: "GBPS" } },
        { text: "6Gbps", value: { value: 6, unit: "GBPS" } },
        { text: "7Gbps", value: { value: 7, unit: "GBPS" } },
        { text: "8Gbps", value: { value: 8, unit: "GBPS" } },
        { text: "9Gbps", value: { value: 9, unit: "GBPS" } },
        { text: "10Gbps", value: { value: 10, unit: "GBPS" } },
      ] as { text: string; value: BandWidth }[],
      routeOptions: [
        { value: "ON", text: "ON" },
        { value: "OFF", text: "OFF" },
        { value: "CUSTOM", text: "CUSTOM" },
      ],
      locationOptions: [
        { value: "EAST_JAPAN", text: "東日本" },
        { value: "WEST_JAPAN", text: "西日本" },
      ],
      /** Maximum Prefix上限値(変更の場合用) */
      maximumPrefixLimit: null as UpperLimitEntity | null,
      /** 1G超VNコネクト */
      vnConnects1G: null as UpperLimitEntity | null,
      editRemainingBandwidth: 0 as number | null,
    };
  },
  computed: {
    /** VNコネクト(Act)名編集可否 */
    isVnConnectNameActEnable() {
      if (this.isAdd) {
        return true;
      }
      return !this.cloudVnOracleDetail!.cloudVnConnectAct.isVnConnectStatus;
    },

    /** VNコネクト(Sby)名編集可否 */
    isVnConnectNameSbyEnable() {
      // 追加あるいはSby自体が存在しない場合
      if (this.isAdd || !this.cloudVnOracleDetail!.cloudVnConnectSby) {
        return true;
      }
      return !this.cloudVnOracleDetail!.cloudVnConnectSby.isVnConnectStatus;
    },
    /** 相関チェック
     * false: Sbyの登録とAct、Commonの編集は同時に行う場合 */
    multipleOption(): boolean {
      if (
        this.isAdd ||
        this.cloudVnOracleDetail!.cloudVnConnectSby ||
        !this.form.cloudVnConnectSby?.serviceKey
      ) {
        return true;
      }
      let changed = this.$crossValidation.objectChanged(
        this.cloudVnOracleDetail!.cloudVnConnectCommon,
        this.form.cloudVnConnectCommon!
      );
      if (!changed) {
        changed = this.$crossValidation.objectChanged(
          this.cloudVnOracleDetail!.cloudVnConnectAct,
          this.form.cloudVnConnectAct!
        );
      }
      return !changed;
    },
    currentLength(): number {
      return this.form.cloudVnConnectCommon!.routeAggregationAddressList
        ? this.form.cloudVnConnectCommon!.routeAggregationAddressList.length
        : 0;
    },
    /** true: 新規, false: 変更 */
    isAdd(): boolean {
      return !this.cloudVnOracleDetail;
    },

    vpnVnCodeSelectList(): {
      value: string;
      text: string;
    }[] {
      return this.vpnVnCodeList.map((v) => {
        if (v.vnName) {
          return {
            value: v.vpnVnCode,
            text: v.vnName + ":" + v.vpnVnCode + "(L3)",
          };
        } else {
          return { value: v.vpnVnCode, text: v.vpnVnCode };
        }
      });
    },
    /** 品目の選択肢 */
    bandwidthSelectList(): { text: string; value: BandWidth }[] {
      // クラウド帯域値
      const limit1 = this.$filter.bandwidthToBps(this.cloudLineMaxBandwidth);
      // 1G超VNコネクト
      const limit2 = this.$filter.bandwidthToBps({
        value: this.vnConnects1G!.upperLimit,
        unit: "GBPS",
      });
      return this.bandwidthOptions.filter((v) => {
        const bps = this.$filter.bandwidthToBps(v.value)!;
        return (
          bps <= limit1! &&
          bps <= limit2! &&
          bps <= this.editRemainingBandwidth!
        );
      });
    },
    /** Maximum Prefixの選択肢 */
    maximumPrefixSelectList(): { text: string; value: number }[] {
      if (this.isAdd) {
        return [{ value: 100, text: "100" }];
      } else {
        return [
          { value: 100, text: "100" },
          { value: 200, text: "200" },
          { value: 300, text: "300" },
          { value: 400, text: "400" },
          { value: 500, text: "500" },
          { value: 1000, text: "1000" },
          { value: 1500, text: "1500" },
          { value: 2000, text: "2000" },
        ].filter((v) => v.value <= this.maximumPrefixLimit!.upperLimit);
      }
    },
    vpnVnName(): string {
      if (
        this.cloudVnOracleDetail!.cloudVnConnectCommon.vpnVnCode.startsWith("D")
      ) {
        return (
          this.cloudVnOracleDetail!.cloudVnConnectCommon.vnName +
          ":" +
          this.cloudVnOracleDetail!.cloudVnConnectCommon.vpnVnCode +
          "(L3)"
        );
      } else {
        return this.cloudVnOracleDetail!.cloudVnConnectCommon.vpnVnCode;
      }
    },
    /** true: Sbyに関する項目は非活性にする */
    isDisableRoutingSby(): boolean {
      return !(
        (this.cloudVnOracleDetail &&
          this.cloudVnOracleDetail.cloudVnConnectSby) ||
        this.form.cloudVnConnectSby?.serviceKey
      );
    },
    /** true: Sbyに関する項目はValidation必要 */
    isRequireRoutingSby(): boolean {
      // Sby情報はdisabledの場合、false
      return !this.isDisableRoutingSby;
    },
    /** true:回線詳細にSby情報が登録済みの場合 */
    hasSby(): boolean {
      return !!(
        this.cloudVnOracleDetail && this.cloudVnOracleDetail.cloudVnConnectSby
      );
    },
    /** 経路集約アドレスの重複チェック用リスト作成 */
    routingAddressList() {
      return (index: number) => {
        return this.form.cloudVnConnectCommon!.routeAggregationAddressList
          ? this.form.cloudVnConnectCommon!.routeAggregationAddressList.filter(
              (_, num) => num !== index
            )
          : [];
      };
    },
  },
  watch: {
    /** 経路集約がCUSTOMに変更されて初期値が存在しない場合は初期化 */
    "form.cloudVnConnectCommon.routeAggregation"(newValue: RouteAggregation) {
      if (
        newValue === "CUSTOM" &&
        (!this.form.cloudVnConnectCommon!.routeAggregationAddressList ||
          this.form.cloudVnConnectCommon!.routeAggregationAddressList.length ===
            0)
      ) {
        this.form.cloudVnConnectCommon!.routeAggregationAddressList = [""];
      }
    },
  },
  async mounted() {
    this.maxBandwidth();
    await this.load();
    this.isLoaded = true;
  },
  methods: {
    async load() {
      // 回線情報に紐づくクラウド回線情報を取得APIを呼び出す
      const lines = (await this.$api.cloudLine.getCloudLines()).lineList;
      const cloudLines = lines.flatMap((v) => v.cloudLineList);
      // クラウド回線の帯域最大値(品目の上限値制御用)
      this.cloudLineMaxBandwidth = cloudLines.find(
        (v: { cloudLineSeq: string }) => v.cloudLineSeq === this.cloudLineSeq
      )!.bandwidth;
      // 1G超VNコネクトの値を取得（品目の上限値制御用）
      this.vnConnects1G = (
        await this.$api.upperLimit.getUpperLimitLine({
          upperLimitLineManageUnit: "MULTI_CLOUD",
          cloudLineSeq: this.cloudLineSeq,
        })
      ).cloudLine!.upperLimitList!.find(
        (v: { upperLimitItemName: string }) =>
          v.upperLimitItemName === "1G超VNコネクト"
      )!;

      if (this.isAdd) {
        this.vpnVnCodeList = (await this.$api.information.getVpnVnCodeList())
          .filter(
            (v: { vnType: string | null }) =>
              v.vnType === "L3" || v.vnType === null
          )
          .sortBy(["vpnVnCode", "asc"]);
      } else {
        // maximumPrefixの上限値を取得
        this.maximumPrefixLimit = (
          await this.$api.upperLimit.getUpperLimitLine({
            upperLimitLineManageUnit: "MULTI_CLOUD",
            cloudLineSeq: this.cloudLineSeq,
            wnumber: this.cloudVnOracleDetail!.cloudVnConnectAct.wnumber,
          })
        ).cloudLine!.upperLimitList!.find(
          (v: { upperLimitItemName: string }) =>
            v.upperLimitItemName === "BGP4の最大経路数"
        )!;
      }
    },

    /** 設定可能帯域の算出 */
    maxBandwidth() {
      if (!this.isAdd) {
        // 編集の場合、設定可能帯域に現在の設定値を加算
        this.editRemainingBandwidth =
          this.remainingBandwidth +
          this.$filter.bandwidthToBps(
            this.cloudVnOracleDetail!.cloudVnConnectCommon.bandwidth
          )!;
      } else {
        // 追加の場合、設定可能帯域をそのまま利用
        this.editRemainingBandwidth = this.remainingBandwidth;
      }
      return this.editRemainingBandwidth;
    },

    /** 経路集約アドレス削除 */
    removeRouteAddress(index: number, remove: (idx: number) => void) {
      const list = this.form.cloudVnConnectCommon!.routeAggregationAddressList!;
      if (list.length > 1) {
        list.splice(index, 1);
        // ValidationにFieldを削除することを伝える
        remove(index);
      } else {
        this.form.cloudVnConnectCommon!.routeAggregationAddressList = [""];
      }
    },

    /** 経路集約アドレス追加 */
    addRouteAddress(push: (item: any) => void) {
      const addItem = "";
      this.form.cloudVnConnectCommon!.routeAggregationAddressList = [
        ...this.form.cloudVnConnectCommon!.routeAggregationAddressList!,
        addItem,
      ];
      // ValidationにFieldを追加することを伝える
      push(addItem);
    },

    async submit() {
      const form = {
        ...this.form,
        cloudVnConnectCommon: {
          ...this.form.cloudVnConnectCommon,
          routeAggregationAddressList:
            this.form.cloudVnConnectCommon!.routeAggregation === "ON"
              ? []
              : this.form.cloudVnConnectCommon!.routeAggregation === "OFF"
                ? null
                : this.form.cloudVnConnectCommon!.routeAggregationAddressList,
        },
      } as
        | CloudVnOraclePost
        | (CloudVnOraclePut & {
            cloudVnConnectSby: { serviceKey: string; asNumber: number };
          });
      //Maximum Prefix(Act)の値をMaximum Prefix(Sby)に代入する
      form.cloudVnConnectSby!.maximumPrefix =
        form.cloudVnConnectAct!.maximumPrefix;
      if (this.isAdd) {
        // 追加の場合
        // 確認モーダルの表示
        let displayVnName;
        const vnSbyServiceKey = form.cloudVnConnectSby?.serviceKey;
        (form as CloudVnOraclePost).cloudVnConnectCommon!.vpnVnCode =
          this.selectedVpnVn!;
        displayVnName = this.vpnVnCodeList.find(
          (v) =>
            v.vpnVnCode ===
            (form as CloudVnOraclePost).cloudVnConnectCommon!.vpnVnCode
        )!.vnName;
        await this.$modal.show(MultiCloudOracleVnConnectionConfirm, {
          cloudVnOracleDetail: {
            cloudVnConnectCommon: {
              ...form.cloudVnConnectCommon,
              vnName: displayVnName,
              routingType: "BGP4",
            },
            cloudVnConnectAct: {
              ...form.cloudVnConnectAct,
              localPreference: "HIGH_PRIORITY",
              med: "LOW_PRIORITY",
            },
            cloudVnConnectSby: vnSbyServiceKey
              ? {
                  ...form.cloudVnConnectSby,
                  localPreference: "LOW_PRIORITY",
                  med: "HIGH_PRIORITY",
                }
              : null,
          },
          displayType: "A",
        });
        await this.$api.cloudVnOracle.postVnConnect(
          this.cloudLineSeq,
          (form.cloudVnConnectSby?.serviceKey
            ? form
            : { ...form, cloudVnConnectSby: null }) as CloudVnOraclePost
        );
        await (
          this.$refs.modal as typeof VueFinalModal & { ok: () => void }
        ).ok();
      } else {
        if (
          !this.cloudVnOracleDetail!.cloudVnConnectSby?.serviceKey &&
          form.cloudVnConnectSby?.serviceKey
        ) {
          // Sbyのみ追加の場合
          // 確認モーダルの表示
          await this.$modal.show(MultiCloudOracleVnConnectionConfirm, {
            cloudVnOracleDetail: {
              cloudVnConnectCommon: form.cloudVnConnectCommon,
              cloudVnConnectAct: form.cloudVnConnectAct,
              cloudVnConnectSby: {
                ...form.cloudVnConnectSby,
                localPreference: "LOW_PRIORITY",
                med: "HIGH_PRIORITY",
              },
            },
            displayType: "M",
          });
          await this.$api.cloudVnOracle.postVnConnect(this.cloudLineSeq, {
            cloudVnConnectCommon: null,
            cloudVnConnectAct: null,
            cloudVnConnectSby: {
              ...form.cloudVnConnectSby,
              pairCloudVnConnectSeq:
                this.cloudVnOracleDetail!.cloudVnConnectAct.cloudVnConnectSeq,
            },
            cloudVnOracleKeyId: this.cloudVnOracleDetail!.cloudVnOracleKeyId,
          } as CloudVnOraclePost);
        } else {
          // 変更のみの場合
          // 確認モーダルの表示
          const vnSbyServiceKey = form.cloudVnConnectSby?.serviceKey;
          await this.$modal.show(MultiCloudOracleVnConnectionConfirm, {
            cloudVnOracleDetail: {
              ...form,
              cloudVnConnectSby: vnSbyServiceKey
                ? form.cloudVnConnectSby
                : null,
            },
            // 確認画面で品目をGbpsからGbpsへ変更した場合に注意文言を表示するため、変更前の品目の値を格納
            beforeBandWidth:
              this.cloudVnOracleDetail?.cloudVnConnectCommon.bandwidth,
            displayType: "M",
          });
          await this.$api.cloudVnOracle.putVnConnect(
            this.cloudLineSeq,
            this.cloudVnOracleDetail!.cloudVnConnectAct.wnumber,
            (this.cloudVnOracleDetail!.cloudVnConnectSby?.serviceKey
              ? form
              : { ...form, cloudVnConnectSby: null }) as CloudVnOraclePut
          );
        }

        await (
          this.$refs.modal as typeof VueFinalModal & { ok: () => void }
        ).ok();
      }
    },
  },
});
</script>
